import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SuccessMessage.module.css';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CustomButton from '../buttons/CustomButton';

const SuccessMessage = ({
  title,
  message,
  redirectPath,
  redirectLabel,
  autoRedirectDelay = 5,
  onClose,
  // Chamado uma única vez, imediatamente antes do redirecionamento (seja pelo fim
  // da contagem ou pelo clique no botão). Permite que o chamador "ative" algo
  // (ex.: gravar sessão/token) só no momento de sair desta tela, e não antes,
  // evitando que efeitos disparados por essa ativação derrubem a mensagem
  // antes que ela possa ser lida.
  onBeforeRedirect,
  // Tela inteira clara com o logo acima do cartão (visual da configuração em foco),
  // em vez do quadro sobre fundo escuro. Ex.: "E-mail confirmado".
  fullPage = false,
  logoSrc
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(autoRedirectDelay);
  const hasRedirectedRef = useRef(false);

  const handleRedirect = () => {
    // Evita disparar onBeforeRedirect/navigate duas vezes (ex.: contagem chega a
    // zero no mesmo instante em que o usuário clica no botão de continuar)
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    if (onBeforeRedirect) {
      onBeforeRedirect();
    }
    if (redirectPath) {
      navigate(redirectPath);
    }
    if (onClose) {
      onClose();
    }
  };

  // Contagem regressiva de 1 em 1 segundo.
  useEffect(() => {
    if (autoRedirectDelay <= 0) return undefined;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // Executa apenas uma vez, ao montar: autoRedirectDelay é o valor inicial da
    // contagem e não deve reiniciar o intervalo se mudar entre renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O redirecionamento ao chegar em zero acontece num efeito separado (depois da
  // renderização), em vez de dentro do updater de setCountdown acima, para não
  // disparar navigate()/setState em cima de outro componente durante a fase de
  // render (o React acusa isso como "Cannot update a component while rendering
  // a different component").
  useEffect(() => {
    if (autoRedirectDelay > 0 && countdown === 0) {
      handleRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);
  
  return (
    <div className={fullPage ? styles.page : styles.overlay}>
      {fullPage && logoSrc && <img className={styles.pageLogo} src={logoSrc} alt="Gabriela" />}
      <div className={`${styles.container} ${fullPage ? styles.pageCard : ''}`}>
        <div className={styles.iconContainer}>
          <CheckCircleOutlineIcon className={styles.icon} />
        </div>
        
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.message}>{message}</p>
        
        {redirectPath && (
          <div className={styles.redirectInfo}>
            {countdown > 0 ? (
              <p className={styles.countdown}>
                Redirecionando em {countdown} {countdown === 1 ? 'segundo' : 'segundos'}...
              </p>
            ) : null}
            
            <CustomButton
              variant="contained"
              style="primary"
              label={redirectLabel || "Continuar"}
              onClick={handleRedirect}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessMessage;